import {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import mapboxgl from 'mapbox-gl';

import { LayerContext } from 'state/LayerContext';
import { useGetLayerQuery } from 'state/apiSlice';
import {
  selectClosureTimeslots,
  selectRouteMetrics,
  selectScenarioCapacity,
  selectScenarioDaysOfWeekInfo,
  selectScenarioEnd,
  selectScenarioMaxDetours,
  selectScenarioRunUUID,
  selectScenarioSegments,
  selectScenarioStart,
  selectScenarioStatus,
  selectVisualize,
} from 'state/detourSlices';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { selectUser } from 'state/userSlice';
import {
  selectCurrentProjectInfo,
  selectLegendSettings,
  selectMapCenter,
  selectMapStyle,
  selectMapZoom,
  selectProject,
  selectRoadFilters,
  selectRouteSelection,
  selectScenarioSetupUIMode,
  selectSegmentId,
  selectShowBarbs,
  selectTargetDate,
  selectTimeOfDay,
  selectTimelineMetric,
  selectTypicalMonth,
  setMapCenter,
  setMapZoom,
  setRouteSelection,
  setSelectedSegmentId,
  setSlowdownThreshold,
  undoRouteSelection,
} from 'state/workflowSlice';

import { optionallyCall } from 'appUtils';
import { nowUnix } from 'features/common/datetimeUtils';
import { MapContext } from 'features/map/MapContext';
import { useBarbs } from 'features/map/hooks/useBarbs';
import { useMapResize } from 'features/map/hooks/useMapResize';
import { useProjectBoundary } from 'features/map/hooks/useProjectBoundary';
import { useRoadClassFilterTooltip } from 'features/map/hooks/useRoadClassFilterTooltip';
import { MapSettings } from 'features/map/overlays/MapSettings';
import { OutsideBboxPrompt } from 'features/map/overlays/OutsideBboxPrompt';
import ScreenshotExtras from 'features/map/overlays/ScreenshotExtras';
import { RoadClassTooltip } from 'features/map/tooltips/RoadClassTooltip/RoadClassTooltip';
import { useZoomPrompt } from 'features/map/useZoomPrompt';
import {
  addMapLayerInDefinedStack,
  getInitialCenter,
  getNearestSegmentFromMouseEvent,
  getRoadClassLayerFilter,
  getRouteInfoFromMouseEvent,
  getSelectionFromClickEvent,
  handleWindowKeyDownRouteSelectionUndo,
  initMap,
  mapHasLayer,
  mapLayerSafeRemove,
  mapPushVectorSegments,
  mapSourceSafeRemove,
  mergeMapLayerFilters,
  redrawBaseSegments,
  redrawSelectedSegment,
  safeRemoveBookmarks,
  setHoverCursorTypeForHover,
  setVisIfNeeded,
  showBookmarks,
  updateMapLocation,
} from 'features/map/utils/mapCommon';
import { MapControl } from 'features/map/utils/mapControls';
import {
  getSegmentLayerDefinition,
  getViewOnlySegmentLayerDefinition,
  kSegmentLayerArrowDefinition,
} from 'features/map/utils/mapLayerDefinitions';
import { ensureRtlLoaded, visibleProperty } from 'features/map/utils/mapUtils';
import { useSegmentSelectionCrosswalk } from 'features/workflow_common/SegmentSelectionCrosswalk';
import { DetourClosureHoverTooltip } from 'features/workflow_detour/DetourMapTooltip/DetourClosureHoverTooltip';
import { DetourMapResultsTooltip } from 'features/workflow_detour/DetourMapTooltip/DetourMapResultsTooltip';
import {
  Feature,
  FeatureCollection,
  LineString,
  MultiLineString,
} from 'geojson';
import { semi_transparent_null_grey } from 'theme/cemTheme';

import { useCallbackWithErrorHandling } from '../../app/ErrorHandling';
import {
  PROCESS_STATUS,
  ScenarioSetupUIMode,
  TIMELINE_MIN_ZOOM,
  mapboxApiKey,
} from '../../appConstants';
import { LayerRouting, Segment } from '../../appTypes';
import {
  selectDisplayedBookmark,
  selectInLocationEditMode,
  setDisplayedBookmark,
} from '../../state/bookmarkSlices';
import { comparison_color_map } from '../chart/chartOptions';
import { useRouteInfo } from '../common/hooks/useRouteInfo';
import { isCtrlEvent } from '../common/utils';
import { MapLoadingSpinner } from '../map/MapLoadingSpinner';
import {
  latLngToJsonPoint,
  launderLatLng,
  linePerpendicularCap,
} from '../map/utils/geography_utils';
import {
  MIN_MAP_ZOOM,
  icon_size_expression,
  kClosureIconImg,
  kHalfDirectionArrowNameLHD,
  kHalfDirectionArrowNameRHD,
  kMainVisLineOpacityExpression,
  kMetricsHoverId,
  kMetricsLayerId,
  kMetricsLayerSourceLayerId,
  kMetricsSourceId,
  kMetricsSymbolsId,
  kMouseHoverTimeoutMillis,
  kScenarioClosureLayerIds,
  kScenarioNonDetourDashedLayerId,
  kScenarioSegmentCapLayerId,
  kScenarioSegmentFillLayerId,
  kScenarioSegmentLayerId,
  kScenarioSegmentSymbolLayerId,
  kSegmentLayerId,
  kSegmentSourceId,
  kTicksShowHideId,
  make_line_width_expression,
  make_symbol_width_expression,
  thick_line_width_expression,
} from '../map/utils/map_constants';
import { FloatingDiv } from '../spinner/Spinner';
import { TimelineLegend } from '../workflow_timeline/TimelineLegend';
import { reverseRouting, segmentLayerFilter } from './DetourMapRouter';
import DetourMapTooltip from './DetourMapTooltip/DetourMapTooltip';
import { useSelectedScenarioClosureRoute } from './ScenarioDataProvider';
import { useScenarioTravelTimeDataProvider } from './ScenarioTravelTimeProvider';
import {
  TL_METRIC_DTR_VOL,
  counts_absolute_size_expr,
  getDetourMetricOptions,
  metricExprDeepSub,
} from './metricsOptions';
import { formatClosureDurationLabel, formatClosureOffsetLabel } from './utils';

// mapbogxl style should be imported BEFORE Map.css as it must override a handful of styles
import 'mapbox-gl/dist/mapbox-gl.css';

import 'features/map/Map.css';

import styles from '../map/Map.module.css';

mapboxgl.accessToken = mapboxApiKey;

export const kScenarioSegmentSourceId = 'scenario_segments';
export const closureColor = '#FD3D3C';
export const closureFillOpacity = 0.45;
const kNonDetourSourceId = 'nondetour';
const kClosureRouteFilter = ['!=', ['get', 'role'], 'cap'];
const kClosureBandWidth = 2.0;
const kClosureCapWidth = 0.9;

function getScenarioClosureLayerDefinitions(
  capacity: number,
): mapboxgl.AnyLayer[] {
  return [
    {
      id: kScenarioSegmentFillLayerId,
      type: 'line',
      source: kScenarioSegmentSourceId,
      filter: kClosureRouteFilter,
      layout: {
        'line-cap': 'butt',
      },
      paint: {
        'line-color': closureColor,
        'line-opacity': capacity === 0 ? 1 : closureFillOpacity,
        'line-width': make_line_width_expression(kClosureBandWidth),
      },
    },
    {
      id: kScenarioSegmentLayerId,
      type: 'line',
      source: kScenarioSegmentSourceId,
      filter: kClosureRouteFilter,
      layout: {
        'line-cap': 'butt',
      },
      paint: {
        'line-color': closureColor,
        'line-width': make_line_width_expression(kClosureBandWidth),
        'line-dasharray': [0.13, 1],
      },
    },
    {
      id: kScenarioSegmentCapLayerId,
      type: 'line',
      source: kScenarioSegmentSourceId,
      filter: ['==', ['get', 'role'], 'cap'],
      layout: {
        'line-cap': 'butt',
      },
      paint: {
        'line-color': closureColor,
        'line-width': make_line_width_expression(kClosureCapWidth),
      },
    },
    {
      id: kScenarioSegmentSymbolLayerId,
      type: 'symbol',
      source: kScenarioSegmentSourceId,
      filter: kClosureRouteFilter,
      layout: {
        'symbol-placement': 'line-center',
        'icon-image': kClosureIconImg,
        'icon-size': make_symbol_width_expression(0.12),
        'icon-rotation-alignment': 'viewport',
      },
      paint: {
        'icon-color': closureColor,
      },
    },
  ];
}

const kNonDetourLayerDashedDefinition = {
  id: kScenarioNonDetourDashedLayerId,
  type: 'line',
  source: kNonDetourSourceId,
  layout: {
    'line-cap': 'butt',
  },
  paint: {
    'line-color': comparison_color_map,
    'line-opacity': 1,
    'line-width': make_line_width_expression(2.3),
    'line-dasharray': [0, 0.5, 0.8],
  },
};

function isUiModeRouteSelectable(
  uiMode: ScenarioSetupUIMode,
  routeMetrics: boolean,
) {
  return (
    uiMode === ScenarioSetupUIMode.Edit ||
    uiMode === ScenarioSetupUIMode.Create ||
    (uiMode === ScenarioSetupUIMode.Scenarios && routeMetrics)
  );
}

const kColorUpdateMillis = 200;

let lastColorUpdate;
let lastColorUpdateRef;

export function buildScenarioClosureFeatureCollection(
  feature: Feature<LineString | MultiLineString>,
): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: [
      {
        ...feature,
        properties: { ...feature.properties, role: 'route' },
      },
      linePerpendicularCap(feature, true),
      linePerpendicularCap(feature, false),
    ],
  };
}

export function DetourMap({ extras }: { extras?: ReactNode }) {
  // https://docs.mapbox.com/help/tutorials/use-mapbox-gl-js-with-react/

  const dispatch = useAppDispatch();
  // useRef DOM refs and for variables accessed from map callbacks
  const { refMap, mapReady, setMapReady, refStyleReady } =
    useContext(MapContext);
  const refMapContainer = useRef(null); // DOM node reference
  const refRouting = useRef<LayerRouting | undefined>(undefined);
  const refReverseRouting = useRef<LayerRouting | undefined | any>(undefined);
  const refMouseoverTimeout = useRef(null);
  const [hoveredSegment, setHoveredSegment] = useState(undefined);
  const [hoveredClosure, setHoveredClosure] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const {
    roadFilterOpen,
    setRoadFilterOpen,
    anchorEl,
    handleRoadClassFilterClickCallback,
  } = useRoadClassFilterTooltip();

  const { layer } = useContext(LayerContext);
  const selectedSegmentId = useAppSelector(selectSegmentId);
  const selectedRoute = useAppSelector(selectRouteSelection);
  const visualize = useAppSelector(selectVisualize);
  const project = useAppSelector(selectProject);
  const currentMapCenter = useAppSelector(selectMapCenter);
  const mapZoom = useAppSelector(selectMapZoom);
  const targetDate = useAppSelector(selectTargetDate);
  const timeOfDay = useAppSelector(selectTimeOfDay);
  const capacity = useAppSelector(selectScenarioCapacity);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const closureTimeslots = useAppSelector(selectClosureTimeslots);
  const showBarbs = useAppSelector(selectShowBarbs);
  const timelineMetric =
    useAppSelector(selectTimelineMetric) || TL_METRIC_DTR_VOL;
  const inLocationEditMode = useAppSelector(selectInLocationEditMode);
  const currentScenarioUUID = useAppSelector(selectScenarioRunUUID);
  const scenarioStatus = useAppSelector(selectScenarioStatus);
  const displayedBookmark = useAppSelector(selectDisplayedBookmark);
  const scenarioSegments = useAppSelector(selectScenarioSegments);
  const uiMode = useAppSelector(selectScenarioSetupUIMode);
  const max_detours = useAppSelector(selectScenarioMaxDetours);
  const mapStyle = useAppSelector(selectMapStyle);
  const routeMetrics = useAppSelector(selectRouteMetrics);
  const roadFilters = useAppSelector(selectRoadFilters);
  const user = useAppSelector(selectUser);

  const hourlyMaxCount = useRef(max_detours);
  const refUiMode = useRef(uiMode);
  const refRouteMetrics = useRef(routeMetrics);
  useMapResize(refMap.current, refMapContainer);
  useProjectBoundary(userProject.project_boundary);
  const {
    basicRouteInfo,
    feature: selectionFeature,
    savedRoute,
  } = useRouteInfo();
  const legendSettings = useAppSelector(selectLegendSettings);

  const { currentData: scenarioRouteData } = useSelectedScenarioClosureRoute();

  const { nonDetourFeature } = useScenarioTravelTimeDataProvider();
  const zoomPrompt = useZoomPrompt(refMap.current, TIMELINE_MIN_ZOOM);

  useEffect(() => {
    if (!(mapReady && refMap.current && refStyleReady.current)) return;

    const visibility = visualize ? 'visible' : 'none';

    setVisIfNeeded(refMap.current, visibility, kSegmentLayerId);
    setVisIfNeeded(refMap.current, visibility, metricLayerDefinition.id);
    setVisIfNeeded(refMap.current, visibility, metricHoverLayerDefinition.id);
  }, [visualize, mapReady]);

  // make sure segmentLayerId is visible when routeMetrics is on
  useEffect(() => {
    if (!(mapReady && refMap.current && refStyleReady.current)) return;
    if (!routeMetrics) return;
    if (!mapHasLayer(refMap.current, kSegmentLayerId)) return;
    refMap.current.setLayoutProperty(kSegmentLayerId, 'visibility', 'visible');
  }, [routeMetrics, mapReady]);

  const refSelection = useRef({
    segment: selectedSegmentId,
    routeSelection: selectedRoute,
    segments: scenarioSegments,
    scenarioFeature: scenarioRouteData?.feature,
    routeFeature: selectionFeature,
    basicRouteInfo,
    nonDetourFeature,
    currentScenarioUUID,
  });

  const timeOfDayOffset = closureTimeslots[timeOfDay]?.slotIndex;
  const timeOfDayOffsetRef = useRef({
    offset: timeOfDayOffset,
    time: timeOfDay,
  });
  timeOfDayOffsetRef.current = { offset: timeOfDayOffset, time: timeOfDay };
  const refInLocationEditMode = useRef(inLocationEditMode);
  const refEnableRouteSelection = useRef(
    isUiModeRouteSelectable(uiMode, routeMetrics),
  );
  // load segmentData for layer from REST api (or cache)
  const { currentData: layerData } = useGetLayerQuery(layer, { skip: !layer });
  useSegmentSelectionCrosswalk();

  const handleTicksShowHideClickCallback = useBarbs(kMetricsSymbolsId);

  // NOTE: there are some incorrect looking connections in this map
  // Confirmed it is a pre-existing weirdness in our routing map,
  // but it's more noticeable when we're highlighting it for sort closures
  refRouting.current = useMemo(() => layerData?.routing, [layerData]);
  refReverseRouting.current = useMemo(
    () => reverseRouting(layerData?.routing),
    [layerData],
  );

  ensureRtlLoaded();
  const editable =
    uiMode === ScenarioSetupUIMode.Create ||
    uiMode === ScenarioSetupUIMode.Edit;
  const refScenarioReady = useRef<boolean>(
    currentScenarioUUID && scenarioStatus === PROCESS_STATUS.DONE && !editable,
  );

  function getColorExpression(metric) {
    const setting = getDetourMetricOptions(mapStyle, metric);
    const NULL_PLACEHOLDER = 1000000;
    if (!setting) {
      console.error(`Setting not found for metric ${metric}`);
      return undefined;
    }
    const settingsArgs = {
      timeOfDayOffset,
      hourlyMaxCount: Number(hourlyMaxCount.current),
      uses_metric: userProject.uses_metric,
    };
    const selectedType = setting.color_schemes[legendSettings.type];
    const selectedColors = selectedType[legendSettings.scheme];

    const metric_expr = setting.metric_expr(settingsArgs);

    // Discrete mode
    if (legendSettings.type === 'discrete') {
      return [
        'step',
        ['coalesce', metric_expr, NULL_PLACEHOLDER],
        selectedColors[1], // for values below 0
        ...selectedColors,
        NULL_PLACEHOLDER,
        semi_transparent_null_grey,
      ];
    }

    return [
      'interpolate',
      ['linear'],
      ['coalesce', metric_expr, NULL_PLACEHOLDER],
      ...optionallyCall(selectedColors, settingsArgs),
      NULL_PLACEHOLDER,
      semi_transparent_null_grey,
    ];
  }

  function getShowExpression(metric) {
    const setting = getDetourMetricOptions(mapStyle, metric);
    if (!setting) {
      console.error(`Setting not found for metric ${metric}`);
      return undefined;
    }
    const settingsArgs = {
      timeOfDayOffset,
      hourlyMaxCount: Number(hourlyMaxCount.current),
      uses_metric: userProject.uses_metric,
    };
    if (!setting.shown) {
      return true;
    } else {
      const shown = metricExprDeepSub(
        [...setting.shown],
        setting.metric_expr(settingsArgs),
      );
      return shown;
    }
  }

  function getLineWidthExpression(metric) {
    const setting = getDetourMetricOptions(mapStyle, metric);
    if (setting?.width === 'count_absolute') {
      const counts_expr = (base) =>
        counts_absolute_size_expr(
          timeOfDayOffset,
          Number(hourlyMaxCount.current),
          base,
        );
      return [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_MAP_ZOOM,
        counts_expr(1),
        14,
        counts_expr(3),
        16,
        counts_expr(8),
        19,
        counts_expr(20),
      ];
    } else {
      return thick_line_width_expression;
    }
  }

  function getLineOffsetExpression() {
    let sign = -1;
    if (userProject.right_hand_drive) {
      sign = 1;
    }
    return [
      'interpolate',
      ['linear'],
      ['zoom'],
      MIN_MAP_ZOOM,
      sign * 1,
      14,
      sign * 2,
      16,
      sign * 3,
      19,
      sign * 5,
    ];
  }

  function getIconSizeExpression(metric) {
    const setting = getDetourMetricOptions(mapStyle, metric);
    if (setting?.width === 'count_absolute') {
      const counts_expr = (base) =>
        counts_absolute_size_expr(
          timeOfDayOffset,
          Number(hourlyMaxCount.current),
          base,
        );
      return [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_MAP_ZOOM,
        counts_expr(0.4),
        14,
        counts_expr(0.4),
        16,
        counts_expr(0.8),
        19,
        counts_expr(1),
      ];
    } else {
      return icon_size_expression;
    }
  }

  const metricLayerDefinition = {
    id: kMetricsLayerId,
    type: 'line',
    source: kMetricsSourceId,
    'source-layer': kMetricsLayerSourceLayerId,
    layout: {},
    paint: {
      'line-color': getColorExpression(timelineMetric),
      'line-width': getLineWidthExpression(timelineMetric),
      'line-opacity': 0.7,
      'line-offset': getLineOffsetExpression(),
    },
  };

  const metricArrowLayerDefinition = {
    ...kSegmentLayerArrowDefinition,
    id: kMetricsSymbolsId,
    source: kMetricsSourceId,
    'source-layer': kMetricsLayerSourceLayerId,
    paint: {
      'icon-color': getColorExpression(timelineMetric),
      'icon-opacity': kMainVisLineOpacityExpression(0.8),
    },
  };

  metricArrowLayerDefinition.layout['icon-image'] = userProject.right_hand_drive
    ? kHalfDirectionArrowNameRHD
    : kHalfDirectionArrowNameLHD;
  metricArrowLayerDefinition.layout['icon-size'] = getIconSizeExpression(
    timelineMetric,
  ) as any;
  (metricArrowLayerDefinition.layout as any).visibility =
    visibleProperty(showBarbs);

  const metricHoverLayerDefinition = {
    ...metricLayerDefinition,
    id: kMetricsHoverId,
    paint: {
      ...metricLayerDefinition.paint,
      'line-color': '#000000',
      'line-opacity': 0,
    },
  };

  function updateColorExpression() {
    // TODO Giles, selection and results layers should just fully be different layers
    if (!refScenarioReady.current) {
      return;
    }
    lastColorUpdate = nowUnix();
    const metricsFilter = mergeMapLayerFilters(
      getShowExpression(timelineMetric),
      getRoadClassLayerFilter(roadFilters),
    );
    if (metricsFilter) {
      refMap.current.setFilter(kMetricsLayerId, metricsFilter);
      if (mapHasLayer(refMap.current, kMetricsSymbolsId))
        refMap.current.setFilter(kMetricsSymbolsId, metricsFilter);
      if (mapHasLayer(refMap.current, kMetricsHoverId))
        refMap.current.setFilter(kMetricsHoverId, metricsFilter);
    }
    refMap.current.setPaintProperty(
      kMetricsLayerId,
      'line-width',
      getLineWidthExpression(timelineMetric),
    );
    refMap.current.setPaintProperty(
      kMetricsLayerId,
      'line-color',
      getColorExpression(timelineMetric),
    );
    if (MapControl.getValue(kTicksShowHideId)) {
      refMap.current.setPaintProperty(
        kMetricsSymbolsId,
        'icon-color',
        getColorExpression(timelineMetric),
      );
      refMap.current.setLayoutProperty(
        kMetricsSymbolsId,
        'icon-size',
        getIconSizeExpression(timelineMetric),
      );
    }
  }

  function drawNonDetourRoute() {
    const map = refMap.current;
    if (!refStyleReady.current) return;
    const feature = refSelection.current.nonDetourFeature;

    mapLayerSafeRemove(map, kScenarioNonDetourDashedLayerId);
    mapSourceSafeRemove(map, kNonDetourSourceId);
    if (feature) {
      map.addSource(kNonDetourSourceId, {
        type: 'geojson',
        data: feature as any,
      });

      addMapLayerInDefinedStack(map, kNonDetourLayerDashedDefinition as any);
    }
  }

  function dispatchSelectionRedraw(force = false) {
    if (refMap.current) {
      const shouldDrawSelection = refEnableRouteSelection.current;
      redrawSelectedSegment(
        refMap.current,
        mapStyle,
        shouldDrawSelection && refSelection.current.segment,
        shouldDrawSelection && refSelection.current.routeSelection,
        shouldDrawSelection && refSelection.current.routeFeature,
        shouldDrawSelection && refSelection.current.basicRouteInfo,
        force,
        false,
      );
    }
  }

  function dispatchScenarioSegmentsRedraw(force = false) {
    if (refMap.current && refStyleReady.current) {
      kScenarioClosureLayerIds.forEach((layerId) => {
        mapLayerSafeRemove(refMap.current, layerId);
      });
      mapSourceSafeRemove(refMap.current, kScenarioSegmentSourceId);

      if (!refSelection.current.scenarioFeature) return;

      refMap.current.addSource(kScenarioSegmentSourceId, {
        type: 'geojson',
        data: buildScenarioClosureFeatureCollection(
          refSelection.current.scenarioFeature as any,
        ),
      });
      getScenarioClosureLayerDefinitions(capacity).forEach(
        (layerDefinition) => {
          addMapLayerInDefinedStack(refMap.current, layerDefinition);
        },
      );
    }
  }

  const handleSegmentClickCallback = useCallbackWithErrorHandling((event) => {
    // console.log(`handleSegmentClickCallback ${JSON.stringify(event.lngLat)} refSegments.current ${JSON.stringify(refSegments.current.length)}`);
    if (refInLocationEditMode.current) {
      dispatch(setDisplayedBookmark(launderLatLng(event.lngLat)));
      return;
    }

    if (
      // Selection is only available in Edit, Create and Scenarios with routeMetrics on
      !(
        refUiMode.current === ScenarioSetupUIMode.Edit ||
        refUiMode.current === ScenarioSetupUIMode.Create ||
        (refUiMode.current === ScenarioSetupUIMode.Scenarios &&
          refRouteMetrics.current)
      )
    ) {
      return;
    }

    const routeSelectionSegments =
      refSelection.current.basicRouteInfo.waypointSegments;

    const {
      segmentId: newSelectedSegmentId,
      route,
      routingFailed,
    } = getSelectionFromClickEvent(
      event,
      refMap.current,
      refRouting,
      refEnableRouteSelection.current,
      isCtrlEvent(event.originalEvent),
      refSelection.current.segment,
      routeSelectionSegments,
      refUiMode.current === ScenarioSetupUIMode.Edit ||
        refUiMode.current === ScenarioSetupUIMode.Create
        ? ['get', 'closable']
        : undefined, // Only restrict selection if in edit mode
      undefined,
      () => dispatch(undoRouteSelection()),
    );

    if (routingFailed) return; // do nothing

    if (route) {
      dispatch(setRouteSelection(route));
    } else if (route === null) {
      if (refEnableRouteSelection.current && newSelectedSegmentId)
        dispatch(setRouteSelection([newSelectedSegmentId]));
      else dispatch(setRouteSelection(undefined));
    }

    dispatch(setSelectedSegmentId(newSelectedSegmentId));
    dispatch(setSlowdownThreshold(undefined));
  });

  function showMouseTooltipForFeature(feature) {
    setHoveredSegment(feature);
    if (refMouseoverTimeout.current) {
      clearTimeout(refMouseoverTimeout.current.handle);
      refMouseoverTimeout.current = undefined;
    }
  }

  function showClosureTooltip() {
    setHoveredSegment(undefined);
    setHoveredClosure(true);
    if (refMouseoverTimeout.current) {
      clearTimeout(refMouseoverTimeout.current.handle);
      refMouseoverTimeout.current = undefined;
    }
  }

  function hideMouseover() {
    setHoveredSegment(undefined);
    setHoveredClosure(false);
    refMouseoverTimeout.current = undefined;
  }

  const handleMouseLeaveEvent = useCallbackWithErrorHandling((event) => {
    if (!refMouseoverTimeout.current) {
      refMouseoverTimeout.current = {
        handle: setTimeout(hideMouseover, kMouseHoverTimeoutMillis),
        set: nowUnix(),
      };
    } else if (
      nowUnix() - refMouseoverTimeout.current.set >
      kMouseHoverTimeoutMillis * 2
    ) {
      hideMouseover();
    }
  });

  const handleMouseMoveEvent = useCallbackWithErrorHandling(
    (event: mapboxgl.MapMouseEvent) => {
      let nearestSegment: Segment;
      if (mapHasLayer(refMap.current, kMetricsHoverId)) {
        nearestSegment = getNearestSegmentFromMouseEvent(
          refMap.current,
          event,
          kMetricsHoverId,
        );
      }

      const onClosure = kScenarioClosureLayerIds.some(
        (layerId) =>
          mapHasLayer(refMap.current, layerId) &&
          getRouteInfoFromMouseEvent(refMap.current, event, layerId),
      );
      const hasTooltip = !!(nearestSegment || onClosure);
      if (onClosure) showClosureTooltip();
      if (nearestSegment) showMouseTooltipForFeature(nearestSegment);
      if (!hasTooltip) handleMouseLeaveEvent(event);

      setHoverCursorTypeForHover(refMap.current, hasTooltip);
    },
  );

  const handleMapMoveCallback = useCallbackWithErrorHandling(() => {
    const center = {
      lng: refMap.current.getCenter().lng.toFixed(4),
      lat: refMap.current.getCenter().lat.toFixed(4),
    };
    const newZoom = refMap.current.getZoom().toFixed(2);
    dispatch(setMapZoom(newZoom));
    dispatch(setMapCenter(center));
  });

  const handleSourceDataEvent = useCallbackWithErrorHandling(
    (e: mapboxgl.MapSourceDataEvent) => {
      // Given we now rely on the segments being loaded via tiles to render
      // Selected and routes, we have to listen to new tiles being rendered
      // In case the selected segments or any of the route segments are suddenly in view
      if (e.sourceId === kSegmentSourceId) {
        dispatchSelectionRedraw();
      }
    },
  );

  function handleMapLoadCallback() {
    setMapReady(true);
    refStyleReady.current = true;
  }

  function handleKeyStroke(event) {
    // On Ctrl-z, undo the last route selection action
    handleWindowKeyDownRouteSelectionUndo(
      event,
      dispatch,
      refEnableRouteSelection.current,
    );
  }

  function selectableSegmentsLayerDefinition() {
    const ret: any = {
      ...getSegmentLayerDefinition(mapStyle, roadFilters),
      filter: mergeMapLayerFilters(
        segmentLayerFilter(
          refRouting.current,
          refReverseRouting.current,
          refSelection.current.segment,
          refSelection.current.routeSelection,
        ),
        getRoadClassLayerFilter(roadFilters),
      ),
    };
    return ret;
  }

  const handleMapSettingsClickCallback = () => {
    setSettingsOpen(true);
  };

  useEffect(() => {
    // Wipe old map
    if (refMap.current) {
      refMap.current.remove();
      refMap.current = undefined;
      setMapReady(false);
      refStyleReady.current = false;
    }
    const { center, zoom } = getInitialCenter(
      userProject,
      currentMapCenter,
      mapZoom,
    );
    if (center) {
      refStyleReady.current = false;
      initMap(
        refMap,
        refMapContainer,
        center,
        zoom,
        mapStyle,
        true,
        {
          handleMapLoadCallback,
          handleMapMoveCallback,
          handleSegmentClickCallback,
          handleMapSettingsClickCallback,
          handleRoadClassFilterClickCallback,
        },
        dispatch,
        userProject,
      );
      lastColorUpdate = nowUnix();
      refMap.current.on('mousemove', handleMouseMoveEvent);
      refMap.current.addControl(
        new MapControl(
          kTicksShowHideId,
          'Show/hide directionality barbs',
          showBarbs,
          handleTicksShowHideClickCallback,
        ),
      );
      refMap.current.on('sourcedata', handleSourceDataEvent);
    } else {
      console.log(
        'Project defaults not yet loaded, skipping initialisation of map',
      );
    }
  }, [userProject, mapStyle]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Draw clickable and nonclickable segments of map.
   */
  useEffect(
    () => {
      refSelection.current.currentScenarioUUID = currentScenarioUUID;
      if (!(mapReady && refMap.current && refStyleReady.current)) {
        return;
      }
      const scenarioReady =
        currentScenarioUUID &&
        scenarioStatus === PROCESS_STATUS.DONE &&
        !editable;
      refScenarioReady.current = scenarioReady;

      const baseLayers = [];
      if (scenarioReady) {
        const selectableSegmentsDefinition = {
          ...getSegmentLayerDefinition(mapStyle, roadFilters),
          paint: getViewOnlySegmentLayerDefinition(mapStyle, roadFilters).paint,
        };
        baseLayers.push(selectableSegmentsDefinition);
      } else {
        baseLayers.push(
          ...[
            // Display the unselectable segments in a different (nonclickable, dimmer) layer
            selectableSegmentsLayerDefinition(),
            getViewOnlySegmentLayerDefinition(mapStyle, roadFilters),
          ],
        );
      }

      // console.log(`============= useeffect redrawSegments (mapReady: ${mapReady}`);
      // console.log('Redraw base segments');
      mapLayerSafeRemove(refMap.current, kMetricsLayerId);
      mapLayerSafeRemove(refMap.current, kMetricsHoverId);
      mapLayerSafeRemove(refMap.current, kMetricsSymbolsId);
      mapSourceSafeRemove(refMap.current, kMetricsSourceId);
      redrawBaseSegments(
        mapReady,
        refMap.current,
        baseLayers,
        `server/detours/tiles/closable/${layer}`,
        TIMELINE_MIN_ZOOM,
        16,
        true,
      );

      if (scenarioReady) {
        mapPushVectorSegments(
          refMap.current,
          kMetricsSourceId,
          `server/detours/tiles/scenario/${currentScenarioUUID}`,
          [
            metricLayerDefinition,
            metricArrowLayerDefinition,
            metricHoverLayerDefinition,
          ],
          TIMELINE_MIN_ZOOM,
          16,
          false,
          mergeMapLayerFilters(
            getShowExpression(timelineMetric),
            getRoadClassLayerFilter(roadFilters),
          ),
        );
      }
    },
    // we don't need dependency on selectedSegmentId even though we draw the selected statement
    // because (besides initial render) it only erasing/drawing it is handled by handleSegmentClickCallback
    [mapReady, layer, currentScenarioUUID, scenarioStatus, editable], // eslint-disable-line react-hooks/exhaustive-deps
  );

  /**
   * Temporarily hide the red closure view in edit mode
   */
  useEffect(() => {
    if (!(mapReady && refMap.current && refStyleReady.current)) return;

    const visibility = uiMode === ScenarioSetupUIMode.Edit ? 'none' : 'visible';

    if (!mapHasLayer(refMap.current, kScenarioSegmentLayerId)) return;

    kScenarioClosureLayerIds.forEach((closureLayerId) => {
      const current = refMap.current.getLayoutProperty(
        closureLayerId,
        'visibility',
      );
      if (current !== visibility) {
        refMap.current.setLayoutProperty(
          closureLayerId,
          'visibility',
          visibility,
        );
      }
    });
  }, [uiMode, mapReady]);

  /**
   * During closure selection, filter out segments that too far from the current selection.
   */
  useEffect(() => {
    if (
      !(
        mapReady &&
        refMap.current &&
        refStyleReady.current &&
        layerData &&
        refRouting.current &&
        refReverseRouting.current
      )
    ) {
      return;
    }

    const scenarioReady =
      currentScenarioUUID &&
      scenarioStatus === PROCESS_STATUS.DONE &&
      !editable;
    refScenarioReady.current = scenarioReady;
    if (scenarioReady) {
      return;
    }

    // Filter out the segments that shouldn't be selectable
    const routingFilter = segmentLayerFilter(
      refRouting.current,
      refReverseRouting.current,
      selectedSegmentId,
      selectedRoute,
    );
    const filters = mergeMapLayerFilters(
      routingFilter,
      getRoadClassLayerFilter(roadFilters),
    );
    if (filters) refMap.current.setFilter(kSegmentLayerId, filters);
  }, [mapReady, layerData, selectedSegmentId, selectedRoute, roadFilters]);

  useEffect(() => {
    refSelection.current.segment = selectedSegmentId;
    refSelection.current.routeSelection = selectedRoute;
    refSelection.current.segments = scenarioSegments;
    refSelection.current.routeFeature = selectionFeature;
    refSelection.current.basicRouteInfo = basicRouteInfo;
    dispatchSelectionRedraw();
  }, [
    selectedRoute,
    selectedSegmentId,
    scenarioSegments,
    selectionFeature,
    basicRouteInfo,
    scenarioRouteData,
    editable,
  ]);

  useEffect(() => {
    refSelection.current.nonDetourFeature = nonDetourFeature;
    drawNonDetourRoute();
  }, [mapReady, nonDetourFeature]);

  useEffect(() => {
    const feature = scenarioRouteData?.feature;
    refSelection.current.scenarioFeature = feature;
    dispatchScenarioSegmentsRedraw();
  }, [mapReady, scenarioRouteData]);

  useEffect(() => {
    if (refMap.current) {
      const { center } = getInitialCenter(
        userProject,
        currentMapCenter,
        mapZoom,
      );
      refMap.current.jumpTo({ center });
    }
  }, [project]);

  useEffect(() => {
    refUiMode.current = uiMode;
    refRouteMetrics.current = routeMetrics;
    refEnableRouteSelection.current = isUiModeRouteSelectable(
      uiMode,
      routeMetrics,
    );
    dispatchSelectionRedraw();
  }, [uiMode, routeMetrics]);

  useEffect(() => {
    if (mapHasLayer(refMap.current, kSegmentLayerId)) {
      if (
        !lastColorUpdate ||
        nowUnix() - lastColorUpdate > kColorUpdateMillis
      ) {
        clearTimeout(lastColorUpdateRef);
        updateColorExpression();
      } else {
        clearTimeout(lastColorUpdateRef);
        lastColorUpdateRef = setTimeout(
          updateColorExpression,
          kColorUpdateMillis,
        );
      }
    }
  }, [
    timeOfDayOffset,
    timelineMetric,
    targetDate,
    legendSettings,
    roadFilters,
  ]);

  useEffect(() => {
    updateMapLocation(refMap.current, mapZoom, currentMapCenter);
  }, [currentMapCenter, mapZoom]);

  useEffect(() => {
    refInLocationEditMode.current = inLocationEditMode;
    if (mapReady) {
      if (inLocationEditMode) {
        if (!displayedBookmark) {
          dispatch(setDisplayedBookmark({ ...refMap.current.getCenter() }));
        } else {
          const bookmarks = {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: latLngToJsonPoint(displayedBookmark),
              },
            ],
          };
          showBookmarks(refMap.current, bookmarks, mapStyle);
        }
      } else {
        safeRemoveBookmarks(refMap.current);
      }
    }
  }, [mapReady, dispatch, inLocationEditMode, displayedBookmark]);

  useEffect(() => {
    if (refMap.current) {
      refMap.current.resize();
    }
  }, [routeMetrics]);

  useEffect(() => {
    if (max_detours) {
      hourlyMaxCount.current = max_detours;
    } else {
      hourlyMaxCount.current = 1000;
    }
  }, [max_detours]);

  useEffect(() => {
    // Add Key listener to the entire document to capture ctrl z events
    document.addEventListener('keydown', handleKeyStroke);
    // Don't forget to clean up
    return function cleanup() {
      document.removeEventListener('keydown', handleKeyStroke);
    };
  }, [handleKeyStroke]);

  const inScenarioMode =
    currentScenarioUUID && uiMode === ScenarioSetupUIMode.Scenarios;

  const scenarioResultsInProgress =
    inScenarioMode && scenarioStatus === PROCESS_STATUS.PROCESSING;

  const scenarioResultsReady =
    inScenarioMode && scenarioStatus === PROCESS_STATUS.DONE;

  return (
    <div ref={refMapContainer} className={styles.map}>
      <MapLoadingSpinner currentRefMap={refMap} mapReady={mapReady} />
      <MapSettings
        open={settingsOpen}
        handleVisSwitch={(visible) => setSettingsOpen(visible)}
      />
      <RoadClassTooltip
        open={roadFilterOpen}
        handleVisSwitch={(visible) => setRoadFilterOpen(visible)}
        anchorEl={anchorEl}
      />
      {refScenarioReady.current && visualize && (
        <TimelineLegend
          hourlyMaxCount={hourlyMaxCount.current}
          metricsOptions={getDetourMetricOptions(mapStyle, timelineMetric)}
        />
      )}
      {scenarioResultsReady && zoomPrompt && (
        <FloatingDiv>Zoom in to view closure</FloatingDiv>
      )}
      <OutsideBboxPrompt currentRefMap={refMap} />
      {scenarioResultsInProgress && (
        <FloatingDiv>
          Your scenario results will appear here once they&apos;re ready
        </FloatingDiv>
      )}
      <div className={styles.tooltip_stack}>
        {(editable || (!visualize && scenarioResultsReady)) && (
          <DetourMapTooltip viewOnly={!editable} />
        )}
        {hoveredClosure && <DetourClosureHoverTooltip />}
        {scenarioResultsReady && hoveredSegment && (
          <DetourMapResultsTooltip segment={hoveredSegment} />
        )}
        {user.has_hidden_features && <DetourScreenshotExtras />}
        {extras}
      </div>
    </div>
  );
}

function DetourScreenshotExtras() {
  const closureStart = useAppSelector(selectScenarioStart);
  const closureEnd = useAppSelector(selectScenarioEnd);
  const timeOfDay = useAppSelector(selectTimeOfDay);
  const capacity = useAppSelector(selectScenarioCapacity);
  const typicalMonth = useAppSelector(selectTypicalMonth);
  const { startDay, endDay } = useAppSelector(selectScenarioDaysOfWeekInfo);
  const timeslots = useAppSelector(selectClosureTimeslots);
  const duration = formatClosureDurationLabel(
    closureStart,
    closureEnd,
    startDay,
    endDay,
  );
  const time = formatClosureOffsetLabel(timeOfDay, timeslots);

  const info = useMemo(() => {
    return {
      time,
      duration,
      capacity: `${capacity}%`,
      time_period: `${typicalMonth}`,
    };
  }, [time, duration, capacity, typicalMonth]);

  return <ScreenshotExtras info={info} />;
}
